/**
 * SEOHead — single source of truth for per-page SEO tags.
 *
 * Renders <Helmet> with title / description / canonical / OG / Twitter
 * cards + optional JSON-LD. Use on every page that we want indexed.
 *
 *   <SEOHead
 *     title="Sell Your Cards Instantly — BLK PACKS Buyback"
 *     description="…"
 *     path="/sell"
 *     ogImage="/images/og-share-card.png"
 *     jsonLd={{ "@context": "https://schema.org", ... }}
 *   />
 *
 * Defaults to the production canonical base (blkpacks.fun). The preview
 * domain never gets indexed because robots/sitemap point only at prod.
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const CANONICAL_BASE = "https://www.blkpacks.fun";
const DEFAULT_OG = "/images/og-share-card.png";
const SITE_NAME = "BLK PACKS";
const TWITTER_HANDLE = "@blkpacks";

export default function SEOHead({
  title,
  description,
  path = "",
  ogImage = DEFAULT_OG,
  ogType = "website",
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Digital + Physical Trading Cards`;
  const canonical = `${CANONICAL_BASE}${path || ""}`;
  const ogImgAbs = ogImage?.startsWith("http") ? ogImage : `${CANONICAL_BASE}${ogImage}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImgAbs} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImgAbs} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
